import * as types from "../constants/ActionTypes";

import {addError} from "./index";

import {MultiStreamer} from "abusesa-js/DataStreamer";
import setImmediate from "abusesa-js/time/setImmediate";

import multixhr from "kpi/common/MultiXHR";
import obj2event from "lib/obj2event";

import URI from "abusesa-js/URI";

function fetchDataset(dispatch, {name, url, keytypes, streamers = [multixhr, "event-stream"]}) {
    dispatch({
        type: types.INIT_DATASET,
        url,
        name,
        keytypes
    });

    if (name.indexOf("file:") === 0) {
        dispatch({
            type: types.COMPLETED_DATASET,
            failed: true,
            reason: `Could not access local file: ${name.slice("file:".length)}. Please drag&drop it again.`,
            name
        });

        return;
    }

    let data = [], timeout = null, gotData = false;

    let doUpdate = () => {
        clearTimeout(timeout);
        timeout = null;

        dispatch({
            type: types.APPEND_TO_DATASET,
            data,
            name
        });

        data = [];
    };

    let update = () => {
        if (!timeout) {
            timeout = setTimeout(doUpdate, 500);
        }
    };

    let eventConverter = obj2event();

    new MultiStreamer(url, {
        streamers: streamers,
        onData: function(item) {
            gotData = true;
            setImmediate(() => {
                data.push(eventConverter(item));
                update();
            });
        },
        onEnd: function() {
            setImmediate(() => {
                doUpdate();
                dispatch({
                    type: types.COMPLETED_DATASET,
                    name
                });
            });

            if (!gotData) {
                dispatch(addError("could not parse file: " + name));
            }
        },
        onError: function(err) {
            dispatch({
                type: types.COMPLETED_DATASET,
                failed: true,
                reason: err.message,
                name
            });

            dispatch(addError(`Error while tring to fetch ${name} ${err.message}`));
        }
    });
}

export function removeDataset(name) {
    return (dispatch, getState) => {
        if (getState().datasets[name]) {
            dispatch({
                type: types.REMOVE_DATASET,
                name
            });
        }
    };
}

function isRelativeUrl(url) {
    try {
        new URI(url);
        return false;
    } catch (e) {
        return url[0] !== "/";
    }
}

export function insertPlaceholderDataset(dataset, base = null) {
    let name, url, granularities, timerange, keytypes;

    if (typeof dataset === "string") {
        name = url = dataset;
    } else if (dataset.name && dataset.url) {
        name = dataset.name;
        url = dataset.url;
        timerange = dataset.timerange;
        granularities = dataset.granularities;
        keytypes = dataset.types;
    } else {
        throw new Error("Invalid dataset url:  " + dataset);
    }

    if (base && isRelativeUrl(url)) {
        base = base.slice(0, base.lastIndexOf("/") + 1);
        url = base + url;
        if (granularities) {
            for (let g of Object.keys(granularities)) {
                granularities[g] = base + granularities[g];
            }
        }
    }

    return {
        type: types.INIT_DATASET,
        name,
        url,
        placeholder: true,
        granularities,
        timerange,
        keytypes
    };
}

const GRANULARITY_ORDER = ["year", "quarter", "month", "week", "day", "hour"];

const pickGranularity = ({granularities, timerange}, wanted, timespan) => {
    let available = Object.keys(granularities);

    if (GRANULARITY_ORDER.includes(wanted)) {
        for (let pick of GRANULARITY_ORDER.slice(GRANULARITY_ORDER.indexOf(wanted))) {
            if (available.includes(pick)) {
                return pick;
            }
        }
    }

    let calcsubset = (r1, r2) => {
        let map = i => r => (r || [])[i];
        let chk = n => !!n && Number(n) > 0;

        let start = [r1, r2].map(map(0)).filter(chk);
        let end = [r1, r2].map(map(1)).filter(chk);
        return [
            Math.max(...start),
            Math.min(...end)
        ];
    };

    if (timerange || timespan) {
        let span = calcsubset(timerange, timespan);
        let days = (span[1] - span[0]) / (24 * 3600 * 1000);
        return days < 9 ? "hour" :
            days < 90 ? "day" :
                "month";
    }

    return null;
};

const datasetKey = (name, granularity, dropkeys, timespan) => {
    return name
        + (granularity ? "#granularity=" + granularity : "")
        + (dropkeys.length ? "#dropkeys=" + dropkeys : "")
        + (timespan && timespan.length ? "#timespan=" + timespan : "");
};

const emptyDataset = {keys: {}, data: [], done: false, name: undefined};

const getDropKeys = (keytypes = {}, selectedKeys = []) => {
    const nonCountTypes = ["time", "key"];

    if (selectedKeys.some(key => keytypes[key] == "uniques")) {
        return Object.keys(keytypes)
            .filter(key => nonCountTypes.includes(keytypes[key]) && !selectedKeys.includes(key));
    }

    return [];
};

export function selectDataset(datasets, name, {granularity, keys, timespan}={}) {
    let dataset = datasets[name];

    if (dataset && dataset.granularities) {
        granularity = pickGranularity(dataset, granularity, timespan);

        let dropkeys = getDropKeys(dataset.keytypes, keys);
        dataset = datasets[datasetKey(name, granularity, dropkeys, timespan)];
    }

    return dataset || {...emptyDataset};
}

export function insertDataset(name, data) {
    name = ("" + name).replace("#", "%23");
    return {
        type: types.INSERT_DATASET,
        name,
        data
    };
}

export function fetchIfNeeded(name, {granularity, keys, timespan} = {}) {
    return (dispatch, getState) => {
        name = ("" + name).replace("#", "%23");

        let datasets = getState()['datasets'];
        let dataset = datasets[name];

        if (dataset === void 0 || dataset.placeholder === true) {
            let url, keytypes;
            if (dataset && dataset.granularities) {
                granularity = pickGranularity(dataset, granularity, timespan);

                url = dataset.granularities[granularity];

                keytypes = dataset.keytypes;
                let dropkeys = getDropKeys(keytypes, keys);
                if (dropkeys.length) {
                    url += "&drop_keys=" + dropkeys;
                }

                if (timespan) {
                    let [start, end] = timespan;
                    if (start) {
                        url += "&start=" + Math.floor(start / 1000);
                    }

                    if (end) {
                        url += "&end=" + Math.floor(end / 1000);
                    }
                }

                name = datasetKey(name, granularity, dropkeys, timespan);

                if (datasets[name]) {
                    return;
                }
            } else {
                url = dataset && dataset.placeholder ? dataset.url : name;
            }

            fetchDataset(dispatch, {url, name, keytypes});
        }
    };
}


// WEBPACK FOOTER //
// ./src/kpi/store/actions/DataActions.js